import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { NotificationType } from 'design-angular-kit';
import { Observable, of, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { CONFIG } from '../../app.config';
import { NotificationService } from '../services/notification.service';
import { AuthService } from '../services/auth.service';
import { TranslateService } from '@ngx-translate/core';
import { toConstantCase } from './utilities';
import { SKIP_SUCCESS_NOTIFICATION, SKIP_FORBIDDEN_NAVIGATION } from './http-context.tokens';

@Injectable()
export class LoginInterceptor implements HttpInterceptor {
  private readonly _notificationService: NotificationService = inject(NotificationService);
  private readonly _authService: AuthService = inject(AuthService);
  private readonly _translateService: TranslateService = inject(TranslateService);

  private readonly router: Router = inject(Router);

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let storageData = localStorage.getItem('t') ?? '';
    const headers: any = {};

    if (storageData) {
      headers['X-CSRF-Token'] = `${storageData}`;
    }

    request = request.clone({
      setHeaders: headers,
      withCredentials: true,
    });

    if (request.url.includes('null')) {
      return of<any>(null);
    }

    return next.handle(request).pipe(
      tap((evt: HttpEvent<any>) => {
        if (evt instanceof HttpResponse) {
          const { method } = request;
          const { status } = evt;

          const isUpdateSuccess =
            ['PUT', 'PATCH', 'DELETE'].includes(method) && (status === 200 || status === 204);

          const isCreateSuccess = method === 'POST' && status === 201;
          const isPostNoContent = method === 'POST' && status === 204;

          const skipNotification = request.context.get(SKIP_SUCCESS_NOTIFICATION);

          if (!skipNotification && (isUpdateSuccess || isCreateSuccess || isPostNoContent)) {
            this._notificationService.createNotification({
              type: NotificationType.Success,
              title: 'NOTIFICATIONS.SUCCESS',
              message: 'NOTIFICATIONS.SAVED_SUCCESSFULLY',
            });
          }
        }
      }),
      catchError((err) => {
        let config = request.context.get(CONFIG);

        if (config.isInitializer) {
          return of<any>(null);
        }

        if (err.status == 401) {
          this._notificationService.createNotification({
            type: NotificationType.Error,
            title: 'NOTIFICATIONS.WARNING',
            message: 'NOTIFICATIONS.ACCESS_DENIED',
          });

          this.router.navigate(['/auth/login']);
          this._authService.setCurrentUser(null);
          localStorage.clear();
        } else if (err.status === 403) {
          // Guards handling an access check return their own UrlTree; history.back() would race that redirect.
          if (!request.context.get(SKIP_FORBIDDEN_NAVIGATION)) {
            history.back();
          }
        } else if (
          err.status === 409 ||
          (err.status === 400 && this.hasValidationTranslation(err.error?.title))
        ) {
          if (err.error?.title?.includes('DOCUMENT_MUST_BE_P7M') ||
              err.error?.title?.includes('UNEXPECTED_DOCUMENTS_UPLOADED') ||
              err.error?.title?.includes('MISSING_REQUIRED_DOCUMENTS')) {
            this.handleDocumentError(err);
          } else if (err.error?.title?.includes('|')) {
            this.handleValidationErrorWithDetails(err);
          } else {
            this._notificationService.createNotification({
              type: NotificationType.Error,
              title: 'NOTIFICATIONS.WARNING',
              message: `VALIDATIONS.${err.error.title}`,
            });
          }
        } else if (err.status != 404) {
          this._notificationService.createNotification({
            type: NotificationType.Error,
            title: 'NOTIFICATIONS.WARNING',
            message: 'NOTIFICATIONS.GENERIC_ERROR',
          });
        }

        return throwError(() => err.error);
      }),
    );
  }

  private hasValidationTranslation(title: unknown): boolean {
    if (typeof title !== 'string' || !title) {
      return false;
    }

    const key = `VALIDATIONS.${title.split('|')[0]}`;
    const translation = this._translateService.instant(key);
    return typeof translation === 'string' && translation !== key;
  }

  private handleValidationErrorWithDetails(err: any) {
    const [errorKey, ...details] = err.error.title.split('|');
    const additionalInfo = details
      .map((detail: string) => detail.startsWith('RAW:') ? detail.slice(4) : detail)
      .filter(Boolean)
      .join(', ');

    this._notificationService.createNotification({
      type: NotificationType.Error,
      title: 'NOTIFICATIONS.WARNING',
      message: `VALIDATIONS.${errorKey}`,
      additionalInfo: additionalInfo ? `: ${additionalInfo}` : '',
    });
  }

  private handleDocumentError(err: any) {
    const splitError = err.error.title.split('|');
    let additionalInfo = splitError
      .slice(1)
      .map((item: string) => {
        if (item.startsWith('RAW:')) {
          const rawValue = item.slice(4);
          return rawValue === '__NEW_DYNAMIC_DOCUMENT__'
            ? this._translateService.instant('VALIDATIONS.DYNAMIC_DOCUMENT_DEFAULT_LABEL')
            : rawValue;
        }

        return this._translateService.instant(`ENUMS.DOCUMENTS.${toConstantCase(item)}`);
      });

    this._notificationService.createNotification({
      type: NotificationType.Error,
      title: 'NOTIFICATIONS.WARNING',
      message: `VALIDATIONS.${splitError[0]}`,
      additionalInfo: additionalInfo.join(', '),
    });
  }
}
